import { Injectable } from '@angular/core';
import { forEach } from 'jszip';
import { StoreService } from 'projects/dop-core/src/public-api';
import { QuotedItem } from 'projects/dop-ordering/src/lib/models/quotable-items/quoted-item.model';
import { CustomerPackage, PackageQuotationLine, SubGroup, TestQuotationLine } from 'projects/dop-ordering/src/public-api';
import { Observable, Subject } from 'rxjs';
import { StoreKey } from 'src/app/core/store/ngrx-store.keys';
import { OrderDataService } from '../data-service/order-data.service';
import { ApplicabilityEnum } from '../models/legacy-matrix-applicability/applicability.enum';
import { CommercialItemApplicability } from '../models/legacy-matrix-applicability/commercial-item-applicability.model';
import { CustomerPackageApplicability } from '../models/legacy-matrix-applicability/customer-package-applicability.model';
import { ProductApplicabilityRequestModel } from '../models/legacy-matrix-applicability/product-applicability-request.model';
import { ProductApplicabilityResponseModel } from '../models/legacy-matrix-applicability/product-applicability-response.model';
import { ProductApplicabilityScRequest } from '../models/legacy-matrix-applicability/product-applicability-sc-request.model';
import { SalesCatalogDisplayData, SalesCatalogueItem } from '../models/sales-catalogue.model';
import { CurrentOrderService } from './current-order.service';

@Injectable({
  providedIn: 'root'
})
export class LegacyMatrixService {

  productApplicabilityResponseModel: ProductApplicabilityResponseModel;

  constructor(
    private orderDataService: OrderDataService,
    private currentOrderService: CurrentOrderService,
    private storeService: StoreService) { }

  async filterMatrixApplicableQuotableItems(legacyMatrixCode: string, analysis: QuotedItem, fromStore = false)
    : Promise<QuotedItem> {
    const request: ProductApplicabilityRequestModel = {
      legacyMatrixCode,
      quotations: [this.currentOrderService.currentOrder.quotation]
    };
    await this.getQuotableItemProductApplicability(JSON.parse(JSON.stringify(request)), fromStore);
    return this.filterAnalyses(analysis);
  }

  async filterMatrixApplicableSalesCatalogs(legacyMatrixCode: string, scCodes: string[], fromStore = false)
    : Promise<ProductApplicabilityResponseModel> {
    const request: ProductApplicabilityScRequest = {
      legacyMatrixCode,
      labSCode: this.currentOrderService.currentOrder?.labDetails?.code,
      accountCode: this.currentOrderService.currentOrder?.account?.code
    };
    return await this.getSalesCatalogProductApplicability(JSON.parse(JSON.stringify(request)), fromStore);
  }

  async getQuotableItemProductApplicability(productApplicabilityRequestModel: ProductApplicabilityRequestModel, fromStore = false)
    : Promise<void> {
    const subject = new Subject<any>();
    if (fromStore) {
      this.productApplicabilityResponseModel =
        await this.storeService.Get<ProductApplicabilityResponseModel>(StoreKey.ProductApplicability).toPromise();
    } else {
      productApplicabilityRequestModel.quotations = productApplicabilityRequestModel.quotations.map(q => {
        q.code = q.code.slice(0, 12);
        return q;
      });
      this.productApplicabilityResponseModel =
        await this.orderDataService.getQuotableItemProductApplicability(productApplicabilityRequestModel).toPromise();
      this.storeService.Save(StoreKey.ProductApplicability, this.productApplicabilityResponseModel, true);
    }

    subject.complete();

    return subject.toPromise();
  }

  async getSalesCatalogProductApplicability(productApplicabilityScRequestModel: ProductApplicabilityScRequest, fromStore = false)
    : Promise<ProductApplicabilityResponseModel> {
    return this.orderDataService.getSalesCatalogProductApplicability(productApplicabilityScRequestModel).toPromise();
  }

  filterAnalyses(analysis: QuotedItem): QuotedItem {
    const allAnalysis: QuotedItem = JSON.parse(JSON.stringify(analysis));
    allAnalysis.quotationCommercialItem.freeQuotationLinesTest =
      this.filterFreeTestLines(allAnalysis.quotationCommercialItem.freeQuotationLinesTest,
        this.productApplicabilityResponseModel?.quotations[0]?.commercialItemApplicabilities);

    allAnalysis.quotationCommercialItem.freeQuotationLinesPackage =
      this.filterFreePackageLines(allAnalysis.quotationCommercialItem.freeQuotationLinesPackage,
        this.productApplicabilityResponseModel?.quotations[0]?.commercialItemApplicabilities);

    allAnalysis.quotationCommercialItem.customerPackages =
      this.filterCustomerPackages(allAnalysis.quotationCommercialItem.customerPackages,
        this.productApplicabilityResponseModel?.quotations[0]?.customerPackageApplicabilities);

    allAnalysis.quotationCommercialItem.commercialItemSubGroups =
      this.filterSubGroups(allAnalysis.quotationCommercialItem.commercialItemSubGroups);
    return allAnalysis;
  }

  filterFreeTestLines(quotationLines: TestQuotationLine[], applicabilityMatrix: CommercialItemApplicability[]): TestQuotationLine[] {
    return !applicabilityMatrix ? [] :
      quotationLines
        .filter((ql: TestQuotationLine) => applicabilityMatrix
          .some(matrix => matrix.commercialItemCode === ql.testCode && matrix.applicability === ApplicabilityEnum.Applicable));
  }

  filterFreePackageLines(quotationLines: PackageQuotationLine[], applicabilityMatrix: CommercialItemApplicability[])
    : PackageQuotationLine[] {
    return !applicabilityMatrix ? [] :
      quotationLines
        .filter((ql: PackageQuotationLine) => applicabilityMatrix
          .some(matrix => matrix.commercialItemCode === ql.packageCode && matrix.applicability === ApplicabilityEnum.Applicable));
  }

  filterCustomerPackages(customerPackages: CustomerPackage[], applicabilityMatrix: CustomerPackageApplicability[]): CustomerPackage[] {
    return !applicabilityMatrix ? [] :
      customerPackages
        .filter((ql: CustomerPackage) => applicabilityMatrix
          .some(matrix => matrix.code === ql.comlimsCode && matrix.applicability === ApplicabilityEnum.Applicable));
  }

  filterSubGroups(subGroups: SubGroup[]): SubGroup[] {
    const subGroupApplicabilityMatrix = this.productApplicabilityResponseModel?.quotations[0]?.subgroupApplicabilities;

    return !subGroupApplicabilityMatrix ? [] :
      subGroups.filter(subGroup => {
        const subGroupApplicability = subGroupApplicabilityMatrix.filter(x => x.code === subGroup.comlimsCode).shift();
        if (subGroupApplicability?.applicability === ApplicabilityEnum.NonApplicable) {
          return false;
        }

        const testOrPackageApplicability = subGroupApplicability?.membersApplicabilities;
        if (!testOrPackageApplicability || testOrPackageApplicability.length === 0) {
          return false;
        }

        subGroup.testQuotationLines = this.filterFreeTestLines(subGroup.testQuotationLines, testOrPackageApplicability);
        subGroup.packageQuotationLines = this.filterFreePackageLines(subGroup.packageQuotationLines, testOrPackageApplicability);
        return subGroup.testQuotationLines?.length > 0 || subGroup.packageQuotationLines?.length > 0;
      });
  }
}
